import LayoutContext from '@/components/Layout/context/LayoutContext/LayoutContext';
import type { MutableRefObject, ReactElement } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';

type SideMenuType = 'more-updates' | 'my-notes' | 'closed';

interface ContextValue {
  createSideMenu?: (child: ReactElement, id: SideMenuType) => void;
  currentId: SideMenuType;
  isVisibleRef: MutableRefObject<boolean>;
  menuElement?: ReactElement | null;
  setShowMenu: (id: SideMenuType) => void;
  showMenu?: boolean;
}

export const SideMenuContext = createContext<ContextValue>({
  currentId: 'closed',
  isVisibleRef: { current: false },
  setShowMenu: () => null,
});

interface Props {
  children: ReactElement;
}

export function SideMenuProvider(props: Props) {
  const { Provider } = SideMenuContext;
  const { setWider, wider } = useContext(LayoutContext);
  const isVisibleRef = useRef<boolean>(false);
  const [currentId, setCurrentId] = useState<SideMenuType>('closed');
  const [menuElement, setMenuElement] = useState<{
    [key: string]: ReactElement;
  } | null>(null);

  const setShowMenu = useCallback(
    (id: SideMenuType) => {
      setCurrentId(id);
      setWider(id !== 'closed');
    },
    [setCurrentId, setWider]
  );

  const createSideMenu = useCallback(
    (child: ReactElement, id: SideMenuType) => {
      setMenuElement((prevState) => ({ ...prevState, [id]: child }));
    },
    [setMenuElement]
  );

  return (
    <Provider
      value={{
        createSideMenu,
        currentId,
        isVisibleRef,
        menuElement: currentId && menuElement ? menuElement[currentId] : null,
        setShowMenu,
        showMenu: wider,
      }}
    >
      {props.children}
    </Provider>
  );
}
